import { CommonModule } from '@angular/common';
import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgxPaginationModule } from 'ngx-pagination';
import { Subject, takeUntil } from 'rxjs';
import {extractErrorMessage, goBack} from 'src/app/common/common';
import { Group } from 'src/app/model/group.model';
import {
  MarketContextCreateDto,
  MarketContextUpdateDto,
  MarketContextViewModel
} from 'src/app/model/market-context.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { SearchFilterModule } from '../../../common/searchFilter/searchFilter.module';

@Component({
  selector: 'app-create-market-context-index',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule, NgxPaginationModule, SearchFilterModule],
  templateUrl: './create-market-context.component.html',
  styleUrl: './create-market-context.component.scss'
})
export class CreateMarketContextComponent implements OnInit, OnDestroy {
  // Search
  searchTextIncludedGroups: string = '';
  searchTextPossibleGroups: string = '';

  // Pagination
  public includedGroupsPagination: any;
  public possibleGroupsPagination: any;

  // Form
  marketContextForm = new FormGroup({
    market_context: new FormControl<string>('', [Validators.required]),
    operator: new FormControl<string>('', [Validators.required]),
    program: new FormControl<string>('', [Validators.required]),
    product: new FormControl<string>(''),
    action_type: new FormControl<string>(''),
    label: new FormControl<string>(''),
  });

  private unsubscribe = new Subject<void>();
  private formSubscribe: any;

  public editing: boolean = false;
  public marketContextId: number = 0;
  public router: Router;
  public error: string | null = null;

  public marketContext: MarketContextViewModel = {
    id: 0,
    market_context: '',
    operator: '',
    program: '',
    product: '',
    events: [],
    vens: [],
    groups: [],
    updated_by: '',
    action_type: '',
    label: '',
  };

  public allGroups: Group[] = [];
  public displayedIncludedGroups: Group[] = [];
  public displayedPossibleGroups: Group[] = [];

  constructor(
    private injector: Injector,
    private marketContextService: ApiService
  ) {
    this.router = injector.get(Router);
    this.router.events.subscribe((event: any) => {
      if (event?.snapshot?.params.id) {
        this.editing = true;
        this.marketContextId = event.snapshot.params.id;
      }
    });

    this.includedGroupsPagination = {
      currentPage: 1,
      itemsPerPage: 6,
      id: 'includedGroupsPagination'
    };

    this.possibleGroupsPagination = {
      currentPage: 1,
      itemsPerPage: 6,
      id: 'possibleGroupsPagination'
    };
  }

  setFormsDefaultValue() {
    this.marketContextForm.controls.market_context.setValue(this.marketContext.market_context);
    this.marketContextForm.controls.operator.setValue(this.marketContext.operator);
    this.marketContextForm.controls.program.setValue(this.marketContext.program);
    this.marketContextForm.controls.product.setValue(this.marketContext.product || '');
    this.marketContextForm.controls.action_type.setValue(this.marketContext.action_type || '');
    this.marketContextForm.controls.label.setValue(this.marketContext.label || '');
  }

  ngOnInit(): void {
    this.marketContextService.findGroups(1).subscribe({
      next: (responseGroups: any) => {
        this.allGroups = responseGroups;

        // If editing, load the market context
        if (this.editing) {
          const queryParams: CustomHttpParams = {
            'depth': 2,
            'id': this.marketContextId
          };
          this.marketContextService.getMarketContext(queryParams).subscribe({
            next: (response: any) => {
              if (response && response.length) {
                this.marketContext = response[0];

                // Ensure group is an array
                if (!this.marketContext.groups) {
                  this.marketContext.groups = [];
                }

                // Setup the included group and possible group
                this.displayedIncludedGroups = [...this.marketContext.groups];

                // Filter out already included group from possible group
                const includedIds = this.displayedIncludedGroups.map(g => g.id);
                this.displayedPossibleGroups = this.allGroups.filter(g => !includedIds.includes(g.id));

                this.setFormsDefaultValue();
              }
            },
            error: (err) => {
              console.error(err);
              this.error = extractErrorMessage(err);
            }
          });
        } else {
          // New market context
          this.displayedPossibleGroups = [...this.allGroups];
        }
      },
      error: (err) => {
        console.error(err);
        this.error = extractErrorMessage(err);
      }
    });

    // Form value change subscriptions
    this.formSubscribe = this.marketContextForm.controls.market_context.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.marketContext.market_context = val || '');

    this.formSubscribe = this.marketContextForm.controls.operator.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.marketContext.operator = val || '');

    this.formSubscribe = this.marketContextForm.controls.program.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.marketContext.program = val || '');

    this.formSubscribe = this.marketContextForm.controls.product.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.marketContext.product = val || '');

    this.formSubscribe = this.marketContextForm.controls.action_type.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.marketContext.action_type = val || '');

    this.formSubscribe = this.marketContextForm.controls.label.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(val => this.marketContext.label = val || '');
  }

  save() {
    if (this.marketContextForm.invalid) {
      this.error = 'Please fill out all required fields correctly and try again.';
      return;
    }

    // Prepare the group data - use the actual Group objects, not just IDs
    const groupsData = [...this.displayedIncludedGroups];

    if (this.editing) {
      const marketContextDto: MarketContextUpdateDto = {
        ...this.marketContext,
        groups: groupsData
      };

      this.marketContextService.editMarketContext(marketContextDto).subscribe({
        next: () => {
          goBack('/marketContexts', this.router);
        },
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err);
        }
      });
    } else {
      // For new market context, don't include the ID (since it's 0)
      const marketContextDto: MarketContextCreateDto = {
        market_context: this.marketContext.market_context,
        operator: this.marketContext.operator,
        program: this.marketContext.program,
        product: this.marketContext.product,
        events: this.marketContext.events,
        vens: this.marketContext.vens,
        groups: groupsData,  // Include the actual group
        updated_by: this.marketContext.updated_by,
        action_type: this.marketContext.action_type,
        label: this.marketContext.label
      };

      this.marketContextService.createMarketContext(marketContextDto).subscribe({
        next: () => {
          goBack('/marketContexts', this.router);
        },
        error: (err) => {
          console.error(err);
          this.error = extractErrorMessage(err);
        }
      });
    }
  }

  backPage(url: string, router: Router) {
    goBack(url, router);
  }

  // Group management
  includeGroup(group: Group) {
    this.displayedIncludedGroups.push(group);
    this.displayedPossibleGroups = this.displayedPossibleGroups.filter(g => g.id !== group.id);
  }

  removeGroup(group: Group) {
    this.displayedIncludedGroups = this.displayedIncludedGroups.filter(g => g.id !== group.id);
    this.displayedPossibleGroups.push(group);
  }

  // Pagination handlers
  handleIncludedGroupsPageChange(event: any) {
    this.includedGroupsPagination.currentPage = event;
  }

  handlePossibleGroupsPageChange(event: any) {
    this.possibleGroupsPagination.currentPage = event;
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
